import { useEffect } from 'react';
import { usePostHog } from 'src/lib/posthog';
import { useLocation } from 'react-router-dom';
import {
  FileTextIcon,
  BookIcon,
  ChatIcon,
  ListChecksIcon,
  UsersThreeIcon,
  CalendarBlankIcon,
  ChartLineIcon,
  PresentationChartIcon,
  PencilSimpleLineIcon,
  ClipboardTextIcon,
  FileArrowUpIcon,
  UserIcon,
  UserGearIcon,
  LinkSimpleIcon,
  BlueprintIcon,
  CalendarDotsIcon,
  BuildingIcon,
  SealCheckIcon,
  QuestionIcon,
  ScrollIcon
} from '@phosphor-icons/react';
import { ResourceLink as ResourceLinkType } from '../types';
import { prefetchResourcePreview, useResourcePreview, type ResourcePreviewMetadata } from '../hooks/useResourcePreview';
import { resolveTenantCode, toTenantPath } from 'src/lib/tenantRouting';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { useOpenAction } from 'src/features/groups/components/ActionViewer';
import { LinkChip } from './LinkChip';
import { RESOURCE_UNAVAILABLE_LABEL, ResourcePopover } from './ResourcePopover';
import { useSpecialist } from 'src/contexts/SpecialistContext';
import { pamHomePath, pamSubmissionPath } from 'src/features/pam/urls';
import { canvasDocumentPath } from '../urls';
import { useCanvasDocumentTarget } from '../hooks/useCanvasDocumentTarget';
import { fetchCanvasDocumentTarget } from '../api';

/**
 * Per-resource-type icon registry. Kept aligned with the icons used in the
 * main navigation (`components/layouts/navbar/menuItems.ts`) and the global
 * search bar (`features/home/components/GlobalSearchBar.tsx`) so a citation
 * of, say, an action reads as visually identical to its nav entry and
 * search result. Unknown types fall back to a generic link icon so new
 * backend resource types keep working without a frontend release.
 */
export const RESOURCE_ICON: Record<string, typeof FileTextIcon> = {
  document: FileTextIcon,
  chat: ChatIcon,
  action: ListChecksIcon,
  obligation: ScrollIcon,
  team_group: UsersThreeIcon,
  meeting: CalendarBlankIcon,
  meeting_series: CalendarDotsIcon,
  review: ClipboardTextIcon,
  asset_review: BlueprintIcon,
  writing: PencilSimpleLineIcon,
  dashboard: PresentationChartIcon,
  metric_node: ChartLineIcon,
  specialist: UserGearIcon,
  action_extraction: FileArrowUpIcon,
  user: UserIcon,
  // SealCheck for an appointment: the statutory "seal of duty".
  training_appointment: SealCheckIcon,
  pam_submission: BuildingIcon,
  pam_submission_question: QuestionIcon
};

/**
 * Maps backend resource type → frontend route. Kept in sync with
 * ``_RESOURCE_TYPE_PATH`` in ``innex_ml.llms.references`` so markdown
 * links rendered server-side (e.g. for canvas export) point at the same
 * routes as in-chat clicks.
 */
const RESOURCE_PATH: Record<string, string> = {
  document: '/wiki',
  chat: '/chat',
  writing: '/writing',
  review: '/review',
  asset_review: '/modules/pfi/asset-review',
  action: '/actions',
  obligation: '/modules/pfi/obligations',
  action_extraction: '/action_extractions',
  team_group: '/groups',
  dashboard: '/reporting/dashboard',
  meeting: '/meetings',
  meeting_series: '/meeting_series',
  // Training Hub appointments don't have per-row pages — the right
  // landing depends on the viewer's permission (tenant admin → the
  // Training Hub Appointments tab; safety-group member → that group's
  // Training tab; appointee → Profile). The server-side preview
  // builder resolves the correct ``open_href`` per viewer and the
  // popover prefers that over this default. The default below is what
  // the inline citation chip falls back to if the preview hasn't
  // loaded yet — Training Hub Appointments is the most-likely-correct
  // target for an admin (the common case for chat citation viewers).
  training_appointment: '/modules/training-hub/appointments'
};

/** Resource types the backend no longer serves; persisted citations of them render as unavailable. */
const RETIRED_RESOURCE_TYPES: ReadonlySet<string> = new Set(['training_certificate']);

export function groupHubResourcePath(groupId: string, resourceType: string, resourceId: string): string | null {
  switch (resourceType) {
    case 'action':
      return `/groups/${groupId}/actions?action=${resourceId}`;
    case 'obligation':
      return `/groups/${groupId}/obligations?obligation=${resourceId}`;
    case 'dashboard':
      return `/groups/${groupId}/dashboards`;
    case 'metric_node':
      return `/groups/${groupId}/dashboards/${resourceId}`;
    case 'training_appointment':
      return `/groups/${groupId}/training?appointment=${resourceId}`;
    case 'meeting':
      return `/meetings/${resourceId}`;
    case 'meeting_series':
      return `/meeting_series/${resourceId}`;
    default:
      return null;
  }
}

export function resourcePath(resourceType: string, resourceId: string): string {
  if (resourceType === 'specialist') {
    return `/chat?open_specialist=${resourceId}`;
  }
  if (resourceType === 'action') {
    return `/actions?action=${resourceId}`;
  }
  if (resourceType === 'obligation') {
    return `/modules/pfi/obligations?obligation=${resourceId}`;
  }
  if (resourceType === 'pam_submission') {
    return pamSubmissionPath(resourceId, { tab: 'questions' });
  }
  // Full URL requires submission ID; resolved server-side via open_href in the
  // preview metadata. Fall back to the PAM module so a direct chip click lands
  // somewhere useful rather than a dead generic resource URL.
  if (resourceType === 'pam_submission_question') {
    return pamHomePath();
  }
  // Training Hub appointments don't have per-row routes — the Training
  // Hub Appointments tab is a flat tenant-wide list. Drop the id
  // segment so the fallback URL points at a valid page; the popover
  // overrides this with a permission-aware ``open_href`` from the
  // preview metadata for the actual click.
  if (resourceType === 'training_appointment') {
    return RESOURCE_PATH[resourceType];
  }
  const base = RESOURCE_PATH[resourceType];
  if (!base) {
    return `/resources/${resourceType}/${resourceId}`;
  }
  return `${base}/${resourceId}`;
}

function resourceTypeLabel(resourceType: string): string {
  return resourceType.replace(/_/g, ' ');
}

const OPEN_LABELS: Record<string, string> = {
  pam_submission: 'Open submission',
  pam_submission_question: 'Open question'
};

export function openResourceLabel(resourceType: string): string {
  return OPEN_LABELS[resourceType] ?? `Open ${resourceTypeLabel(resourceType)}`;
}

/** Upgrade the default ``resourcePath`` URL when preview metadata carries a
 * more specific target (PAM questions, Training Hub rows, meeting series). */
export function resolveResourceHref(metadata: ResourcePreviewMetadata, defaultHref: string, pathname: string): string {
  if ((metadata.kind === 'training_appointment' || metadata.kind === 'pam_submission_question') && metadata.open_href) {
    const tenantCode = resolveTenantCode(pathname);
    return tenantCode ? toTenantPath(metadata.open_href, tenantCode) : metadata.open_href;
  }
  if (metadata.kind === 'obligation') {
    const tenantCode = resolveTenantCode(pathname);
    const query = defaultHref.includes('?') ? defaultHref.slice(defaultHref.indexOf('?')) : '';
    const target = `${
      metadata.group_id ? `/groups/${metadata.group_id}/obligations` : '/modules/pfi/obligations'
    }${query}`;
    return tenantCode ? toTenantPath(target, tenantCode) : target;
  }
  if (metadata.kind !== 'meeting_series' || !metadata.group_id) {
    return defaultHref;
  }
  const tenantCode = resolveTenantCode(pathname);
  const target = `/groups/${metadata.group_id}/meetings`;
  return tenantCode ? toTenantPath(target, tenantCode) : target;
}

/**
 * Renders a single `<resource id='N'>label</resource>` citation inline.
 *
 * Picks an icon and navigation target from the resource type, so documents,
 * actions, team groups, meetings, writings, etc. all get a meaningful
 * visual treatment. Legacy `file_link_*` stream parts are normalised into
 * this shape inside {@link useChatStream}. The label shown to the user is
 * always what the agent wrote between the opening and closing tags.
 */
export function ResourceLink({
  content,
  data,
  maxLabelLength,
  variant = 'chip',
  groupId,
  trackAsChat = true,
  onActivate
}: {
  content: string;
  data: ResourceLinkType;
  maxLabelLength?: number;
  /** `chip` is the in-chat icon pill. `prose` is an underlined inline link. */
  variant?: 'chip' | 'prose';
  /** When set, prefer the group hub view of the resource over the global page. */
  groupId?: string;
  /** Chat citations record `chat_resource_link_clicked`. Other surfaces pass false and use `onActivate`. */
  trackAsChat?: boolean;
  onActivate?: () => void;
}) {
  const posthog = usePostHog();
  const location = useLocation();
  const navigate = useTenantNavigate();
  const openAction = useOpenAction();

  // Specialists are a modal overlay inside the chat, not a standalone
  // page. When clicked from the chat, we intercept the navigation and
  // open the modal in-place so the user stays on the same chat rather
  // than navigating to ``/chat?open_specialist={id}``.
  let specialistOpener: ((id: string) => void) | null = null;
  try {
    const ctx = useSpecialist();
    specialistOpener = ctx.openSpecialistById;
  } catch {
    // Outside SpecialistProvider — fall through to normal link behaviour.
  }

  // Canvas markdown is stored as a document. A click opens that canvas in its
  // chat, the same way global search does, rather than the raw file in Document Hub.
  const canvasDocumentId = data.resource_type === 'document' && !data.is_wiki ? data.resource_id : undefined;
  const canvasLookup = useCanvasDocumentTarget(canvasDocumentId);
  const canvasTarget = canvasLookup.data && canvasLookup.data !== 'denied' ? canvasLookup.data : null;
  const canvasDenied = canvasLookup.data === 'denied';

  const needsPreviewHref = data.resource_type === 'pam_submission_question' || data.resource_type === 'obligation';
  useEffect(() => {
    if (needsPreviewHref) {
      prefetchResourcePreview(data.resource_id);
    }
  }, [needsPreviewHref, data.resource_id]);

  const previewState = useResourcePreview(data.resource_id, needsPreviewHref);

  const tenantCode = resolveTenantCode(location.pathname);
  const groupPath = groupId ? groupHubResourcePath(groupId, data.resource_type, data.resource_id) : null;
  const canvasPathname = canvasTarget ? canvasDocumentPath(canvasTarget) : null;
  const awaitingCanvas = Boolean(canvasDocumentId) && (canvasLookup.isPending || canvasLookup.isError);
  const rawPath =
    canvasPathname ??
    (awaitingCanvas || canvasDenied ? null : (groupPath ?? resourcePath(data.resource_type, data.resource_id)));
  let href = rawPath == null ? '#' : tenantCode ? toTenantPath(rawPath, tenantCode) : rawPath;
  if (rawPath && !canvasPathname && !groupPath && previewState.status === 'ready' && previewState.data) {
    href = resolveResourceHref(previewState.data.metadata, href, location.pathname);
  }

  const Icon = data.is_wiki ? BookIcon : (RESOURCE_ICON[data.resource_type] ?? LinkSimpleIcon);

  if (RETIRED_RESOURCE_TYPES.has(data.resource_type)) {
    return variant === 'prose' ? (
      <span className="cursor-not-allowed opacity-50" title={RESOURCE_UNAVAILABLE_LABEL}>
        {content}
      </span>
    ) : (
      <LinkChip
        icon={Icon}
        label={content}
        title={RESOURCE_UNAVAILABLE_LABEL}
        disabled
        maxLabelLength={maxLabelLength}
      />
    );
  }

  const isSpecialist = data.resource_type === 'specialist';
  const isAction = data.resource_type === 'action';

  const openInNewTab =
    !canvasPathname && !groupPath && (!isSpecialist || !specialistOpener) && (!isAction || !openAction);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // `null` means the lookup finished and this is an ordinary document, so the
    // anchor can open Document Hub in this click. Anything else waits for the
    // lookup, including a click that arrives while it is still running.
    if (canvasDocumentId && canvasLookup.data !== null) {
      event.preventDefault();
      event.stopPropagation();
      const documentPath = groupPath ?? resourcePath(data.resource_type, data.resource_id);
      void fetchCanvasDocumentTarget(canvasDocumentId)
        .then((target) => {
          if (target && target !== 'denied') {
            if (trackAsChat) posthog?.capture('chat_resource_link_clicked', { resourceType: 'canvas' });
            onActivate?.();
            navigate(canvasDocumentPath(target));
            return;
          }
          if (target === null) {
            if (trackAsChat) posthog?.capture('chat_resource_link_clicked', { resourceType: data.resource_type });
            onActivate?.();
            if (groupPath) navigate(groupPath);
            else navigate(documentPath);
          }
        })
        .catch(() => {
          // Leave the link unopened. Falling through to Document Hub would show the raw markdown.
        });
      return;
    }

    if (trackAsChat) {
      posthog?.capture('chat_resource_link_clicked', {
        resourceType: canvasPathname ? 'canvas' : data.resource_type
      });
    }
    onActivate?.();

    if (!rawPath || canvasDenied) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (isSpecialist && specialistOpener) {
      event.preventDefault();
      event.stopPropagation();
      specialistOpener(data.resource_id);
      return;
    }

    if (isAction && openAction) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      event.stopPropagation();
      openAction(data.resource_id);
      return;
    }

    if (groupPath) {
      event.preventDefault();
      event.stopPropagation();
      navigate(groupPath);
      return;
    }

    event.stopPropagation();
  };

  const handleHoverOpen = () => {
    posthog?.capture('chat_resource_link_hovered', {
      resourceType: data.resource_type
    });
  };

  const trigger =
    variant === 'prose' ? (
      <a
        href={href}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        onClick={handleClick}
        className="text-secondary-blue underline underline-offset-2 decoration-secondary-blue/60 hover:decoration-secondary-blue"
      >
        {content}
      </a>
    ) : (
      <LinkChip
        icon={Icon}
        label={content}
        href={href}
        newTab={openInNewTab}
        onClick={handleClick}
        maxLabelLength={maxLabelLength}
      />
    );

  return (
    <ResourcePopover
      resourceId={data.resource_id}
      href={href}
      openLabel={canvasPathname ? 'Open Editor Document' : openResourceLabel(data.resource_type)}
      onOpen={handleHoverOpen}
      onOpenClick={handleClick}
    >
      {trigger}
    </ResourcePopover>
  );
}
